import { StrandTimeSeriesData } from "@/pages/caster/models/StrandSpeed";
import { getMomentTz } from "@dexteel/mesf-core";
import { EChartsOption } from "echarts-for-react";
import { useMemo } from "react";

const colors = ["#E41A1C", "#377EB8", "#4DAF4A", "rgb(196, 174, 209)"];

const moment = getMomentTz();

interface UseChartOptionsProps {
	moldLevels: StrandTimeSeriesData[] | null | undefined;
	heatName: string;
}

const strandColors: { [key: string]: string } = {
	S1: colors[0], // Red
	S2: colors[1], // Blue
	S3: colors[2], // Green
	"Tundish Level": colors[3], // Light Purple
};

export const useChartOptions = ({
	moldLevels,
	heatName,
}: UseChartOptionsProps): EChartsOption => {
	const chartOptions = useMemo<EChartsOption>(() => {
		const allData: StrandTimeSeriesData[] = (moldLevels || [])
			.map((temp) => ({
				Datetime: temp.Datetime,
				Value: temp.Value ?? 0,
				TagId: temp.TagId?.toString() || "",
				TagName: temp.TagName || "",
			}))
			.filter((item) => item.Datetime && item.TagId && item.TagName);

		if (allData.length === 0) {
			return {
				title: {
					text: "No data available",
					left: "center",
					top: "center",
				},
				series: [],
			};
		}

		const moldLevelData = allData.filter(
			(item) => item.TagName !== "Tundish Level",
		);
		const tundishLevelData = allData.filter(
			(item) => item.TagName === "Tundish Level",
		);

		const yAxes: any[] = [];
		const allSeries: any[] = [];

		if (moldLevelData.length > 0) {
			const values = moldLevelData.map((item) => item.Value);
			const minValue = Math.min(...values).toFixed(0);
			const maxValue = Math.max(...values).toFixed(0);
			const margin = Math.ceil((Number(maxValue) - Number(minValue)) * 0.05);
			const yMin = Math.max(0, Math.floor(Number(minValue)) - margin);
			const yMax = Math.ceil(Number(maxValue) + margin);
			const interval = Math.ceil((yMax - yMin) / 5);

			yAxes.push({
				type: "value",
				name: "Mold Level (%)",
				nameTextStyle: {
					fontSize: 14,
					align: "left",
				},
				nameLocation: "end",
				min: yMin,
				max: yMax,
				interval,
			});

			const groupedData = moldLevelData.reduce<
				Record<string, StrandTimeSeriesData[]>
			>((acc, item) => {
				if (!acc[item.TagId]) acc[item.TagId] = [];
				acc[item.TagId].push(item);
				return acc;
			}, {});

			Object.entries(groupedData).forEach(([tagId, tagData]) => {
				const tagName = tagData[0].TagName;
				allSeries.push({
					name: tagName,
					type: "line",
					smooth: true,
					symbol: "none",
					sampling: "lttb",
					yAxisIndex: 0,
					itemStyle: {
						color: strandColors[tagName.substring(0, 2)] || strandColors.S1,
					},
					data: tagData.map((item) => [item.Datetime.getTime(), item.Value]),
				});
			});
		}

		if (tundishLevelData.length > 0) {
			const values = tundishLevelData.map((item) => item.Value);
			const minValue = Math.min(...values).toFixed(0);
			const maxValue = Math.max(...values).toFixed(0);
			const margin = Math.ceil((Number(maxValue) - Number(minValue)) * 0.05);
			const yMin = Math.max(0, Math.floor(Number(minValue)) - margin);
			const yMax = Math.ceil(Number(maxValue) + margin);
			const interval = Math.ceil((yMax - yMin) / 5);

			yAxes.push({
				type: "value",
				name: "Tundish Level (FT)",
				position: "right",
				nameTextStyle: {
					fontSize: 14,
					align: "right",
				},
				nameLocation: "end",
				min: yMin,
				max: yMax,
				interval,
			});

			allSeries.push({
				name: "Tundish Level",
				type: "line",
				smooth: true,
				symbol: "none",
				sampling: "lttb",
				yAxisIndex: yAxes.length - 1,
				areaStyle: {},
				itemStyle: {
					color: strandColors["Tundish Level"],
				},
				data: tundishLevelData.map((item) => [
					item.Datetime.getTime(),
					item.Value,
				]),
			});
		}

		return {
			animation: false,
			toolbox: {
				feature: {
					saveAsImage: {
						name: `Heat ${heatName} - Mold Level`,
						pixelRatio: 2,
					},
				},
			},
			grid: {
				right: tundishLevelData.length > 0 ? "8%" : "5%",
			},
			dataZoom: [
				{
					type: "inside",
					show: true,
				},
			],
			tooltip: {
				trigger: "axis",
				formatter: (params: any) => {
					const datetime = moment(params[0].value[0]).format(
						"yyyy-MM-DD HH:mm:ss",
					);

					const tooltipContent = params
						.map(
							(param: any) =>
								`${param.seriesName}: ${param.value[1].toFixed(1)}`,
						)
						.join("<br>");

					return `Time: ${datetime}<br>${tooltipContent}`;
				},
			},
			legend: {
				data: allSeries.map((s) => s.name).sort(),
				top: "bottom",
				selectedMode: true,
				textStyle: {
					fontSize: 10,
				},
				itemGap: 20,
			},
			xAxis: {
				type: "time",
				axisLabel: {
					formatter: (value: number) => {
						return moment(value).format("HH:mm");
					},
				},
			},
			yAxis: yAxes,
			series: allSeries,
		};
	}, [moldLevels, heatName]);

	return chartOptions;
};
